import { useEffect, useState, useCallback } from "react";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import { Percent, FloppyDisk, Lock, LockOpen, MagnifyingGlass, X, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed((cents || 0) % 100 === 0 ? 0 : 2)}`;
const fmtBps = (bps) => `${(bps / 100).toFixed(2)}%`;

// Feb 19, 2026 — duplicated "Global fee defaults" block removed. Global
// monetary dials now live exclusively in <PricingLab /> at the top of the
// Billing & Earnings tab. This component is now strictly per-creator
// overrides (its actual unique value-add) + search.
export default function AdminFeePanel() {
  const [fees, setFees] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/fees");
      setFees(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load fee settings.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!fees) return <div className="text-sm text-[#6B6A68]">Loading fee settings…</div>;

  return (
    <div className="space-y-6" data-testid="admin-fee-panel">
      {/* Per-creator overrides ============================================ */}
      <CreatorOverrideSearch globalFees={fees} />
    </div>
  );
}

function CreatorOverrideSearch({ globalFees }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const runSearch = async (query) => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get(`/admin/users/search?q=${encodeURIComponent(query)}`);
      setResults(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed.");
    } finally { setSearching(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const patchCreator = async (creator, patch) => {
    try {
      const r = await api.patch(`/admin/users/${creator.id}/fees`, patch);
      setResults((prev) => prev.map((u) => u.id === creator.id ? { ...u, ...r.data } : u));
      toast.success("Override updated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update override.");
    }
  };

  return (
    <div className="bg-white border hairline rounded-lg p-6" data-testid="admin-creator-overrides">
      <div className="flex items-center gap-2 mb-1">
        <Lock size={18} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-xl">Per-creator overrides</h3>
      </div>
      <p className="text-xs text-[#6B6A68] mb-4 max-w-2xl">
        Search by email or name. Lock an override to protect it from future global-default bulk updates.
      </p>
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators (email / name)…"
          data-testid="fees-creator-search"
          className="w-full bg-white border hairline rounded-md pl-9 pr-9 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm" />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B6A68] hover:text-[#1C1B1A]">
            <X size={14} />
          </button>
        )}
      </div>

      {searching && <div className="text-xs text-[#6B6A68] mt-3">Searching…</div>}
      {!searching && q.trim() && results.length === 0 && <div className="text-xs text-[#6B6A68] mt-3">No creators matched.</div>}

      <div className="mt-4 space-y-3" data-testid="fees-override-results">
        {results.map((u) => (
          <CreatorOverrideRow key={u.id} creator={u} globalFees={globalFees} onPatch={patchCreator} />
        ))}
      </div>
    </div>
  );
}

function CreatorOverrideRow({ creator, globalFees, onPatch }) {
  const hasPlatformOverride = creator.platform_fee_cents_override !== undefined && creator.platform_fee_cents_override !== null;
  const hasFanOverride = creator.fan_cut_bps_override !== undefined && creator.fan_cut_bps_override !== null;

  const [platformDollars, setPlatformDollars] = useState(
    hasPlatformOverride ? Math.round(creator.platform_fee_cents_override / 100) : Math.round(globalFees.platform_fee_cents / 100)
  );
  const [fanPercent, setFanPercent] = useState(
    hasFanOverride ? (creator.fan_cut_bps_override / 100) : (globalFees.fan_cut_bps / 100)
  );
  const [platformLocked, setPlatformLocked] = useState(!!creator.platform_fee_override_locked);
  const [fanLocked, setFanLocked] = useState(!!creator.fan_cut_override_locked);

  const savePlatformOverride = () => onPatch(creator, {
    platform_fee_cents_override: platformDollars * 100,
    platform_fee_override_locked: platformLocked,
  });
  const clearPlatformOverride = () => onPatch(creator, { platform_fee_cents_override: null, platform_fee_override_locked: false });
  const saveFanOverride = () => onPatch(creator, {
    fan_cut_bps_override: Math.round(fanPercent * 100),
    fan_cut_override_locked: fanLocked,
  });
  const clearFanOverride = () => onPatch(creator, { fan_cut_bps_override: null, fan_cut_override_locked: false });

  return (
    <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]" data-testid={`override-row-${creator.email}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm font-medium text-[#1C1B1A]">{creator.email}</div>
          <div className="text-xs text-[#6B6A68]">{creator.name} · {creator.silo_count ?? 0} silo(s)</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mt-1">
            Current: {fmt$(hasPlatformOverride ? creator.platform_fee_cents_override : globalFees.platform_fee_cents)}/mo
            · {fmtBps(hasFanOverride ? creator.fan_cut_bps_override : globalFees.fan_cut_bps)} fan cut
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        {/* Platform fee override */}
        <div className="bg-white border hairline rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <Percent size={12} className="text-[#4A5D4E]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A68]">Platform fee · $/mo</span>
            {hasPlatformOverride && <span className="text-[10px] text-[#2F6B3A] ml-auto"><CheckCircle size={10} weight="duotone" className="inline mb-0.5 mr-0.5" /> override set</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#6B6A68]">$</span>
            <FreeNumberInput min={0} max={1000} step={1} value={platformDollars}
              onChange={(v) => setPlatformDollars(v)}
              data-testid={`override-platform-input-${creator.email}`}
              className="w-20 bg-white border hairline rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            <label className="flex items-center gap-1 text-xs text-[#6B6A68] cursor-pointer">
              <input type="checkbox" checked={platformLocked} onChange={(e) => setPlatformLocked(e.target.checked)}
                data-testid={`override-platform-lock-${creator.email}`} />
              {platformLocked ? <Lock size={12} className="text-[#4A5D4E]" /> : <LockOpen size={12} />} Lock
            </label>
            <div className="ml-auto flex gap-1">
              <button onClick={savePlatformOverride} data-testid={`override-platform-save-${creator.email}`}
                className="text-xs px-2.5 py-1 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E]">Save</button>
              {hasPlatformOverride && (
                <button onClick={clearPlatformOverride} data-testid={`override-platform-clear-${creator.email}`}
                  className="text-xs px-2.5 py-1 rounded-md border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A]">Clear</button>
              )}
            </div>
          </div>
        </div>

        {/* Fan cut override */}
        <div className="bg-white border hairline rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <Percent size={12} className="text-[#4A5D4E]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A68]">Fan cut · %</span>
            {hasFanOverride && <span className="text-[10px] text-[#2F6B3A] ml-auto"><CheckCircle size={10} weight="duotone" className="inline mb-0.5 mr-0.5" /> override set</span>}
          </div>
          <div className="flex items-center gap-2">
            <FreeNumberInput min={0} max={50} step={0.5} precision={1} value={fanPercent}
              onChange={(v) => setFanPercent(v)}
              data-testid={`override-fan-input-${creator.email}`}
              className="w-20 bg-white border hairline rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            <span className="text-[#6B6A68] text-sm">%</span>
            <label className="flex items-center gap-1 text-xs text-[#6B6A68] cursor-pointer">
              <input type="checkbox" checked={fanLocked} onChange={(e) => setFanLocked(e.target.checked)}
                data-testid={`override-fan-lock-${creator.email}`} />
              {fanLocked ? <Lock size={12} className="text-[#4A5D4E]" /> : <LockOpen size={12} />} Lock
            </label>
            <div className="ml-auto flex gap-1">
              <button onClick={saveFanOverride} data-testid={`override-fan-save-${creator.email}`}
                className="text-xs px-2.5 py-1 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E]">Save</button>
              {hasFanOverride && (
                <button onClick={clearFanOverride} data-testid={`override-fan-clear-${creator.email}`}
                  className="text-xs px-2.5 py-1 rounded-md border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A]">Clear</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {(platformLocked || fanLocked) && (
        <div className="mt-2 text-[11px] text-[#4A5D4E] flex items-center gap-1">
          <Warning size={12} weight="duotone" /> Locked overrides are skipped by global-default bulk updates.
        </div>
      )}
    </div>
  );
}
